import { forwardRef } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { ArrowRight } from "@/icons";
import { Magnetic } from "@/components/common/Magnetic";

type Variant = "primary" | "secondary" | "ghost" | "outline";
type Size = "default" | "lg";

interface CtaButtonProps {
  to?: string;
  href?: string;
  onClick?: () => void;
  children: React.ReactNode;
  variant?: Variant;
  size?: Size;
  withArrow?: boolean;
  magnetic?: boolean;
  className?: string;
  type?: "button" | "submit";
  disabled?: boolean;
  "data-testid"?: string;
}

const base =
  "group relative inline-flex items-center justify-center gap-2 rounded-full font-medium tracking-tight transition-[background,color,box-shadow,transform] duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:opacity-60 disabled:pointer-events-none";

const variants: Record<Variant, string> = {
  primary:
    "bg-primary text-primary-foreground shadow-[0_10px_30px_-8px_rgba(15,118,110,0.5)] hover:shadow-[0_16px_40px_-10px_rgba(15,118,110,0.55)] hover:-translate-y-0.5",
  secondary:
    "bg-secondary text-secondary-foreground hover:bg-secondary/90 hover:-translate-y-0.5",
  outline:
    "border border-border bg-transparent text-foreground hover:border-foreground/30 hover:bg-foreground/[0.03]",
  ghost: "text-foreground hover:bg-foreground/[0.04]",
};

const sizes: Record<Size, string> = {
  default: "h-11 px-6 text-sm",
  lg: "h-14 px-8 text-base",
};

/** Brand call-to-action. Renders a router Link, anchor, or button. */
export const CtaButton = forwardRef<HTMLButtonElement, CtaButtonProps>(
  (
    {
      to,
      href,
      onClick,
      children,
      variant = "primary",
      size = "default",
      withArrow = false,
      magnetic = false,
      className,
      type = "button",
      disabled,
      ...rest
    },
    ref,
  ) => {
    const classes = cn(base, variants[variant], sizes[size], className);
    const inner = (
      <>
        <span className="relative z-10">{children}</span>
        {withArrow && (
          <ArrowRight className="relative z-10 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        )}
      </>
    );

    let node: React.ReactNode;
    if (to) {
      node = (
        <Link to={to} className={classes} onClick={onClick} {...rest}>
          {inner}
        </Link>
      );
    } else if (href) {
      node = (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={classes}
          onClick={onClick}
          {...rest}
        >
          {inner}
        </a>
      );
    } else {
      node = (
        <button
          ref={ref}
          type={type}
          onClick={onClick}
          disabled={disabled}
          className={classes}
          {...rest}
        >
          {inner}
        </button>
      );
    }

    return magnetic ? <Magnetic>{node}</Magnetic> : <>{node}</>;
  },
);

CtaButton.displayName = "CtaButton";
